//@author: devin
import { ChangeDetectionStrategy, Component, computed, effect, input, ViewEncapsulation } from "@angular/core";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID, IngestionPointCategory } from "../../data-ingestion/data.ingestion.const";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";

@TypeManagerDecorator("s25-ingestion-point-dropdown")
@Component({
    selector: "s25-ingestion-point-dropdown",
    template: `
        @if (items) {
            <s25-generic-dropdown
                [value]="value()"
                [hasFav]="hasFav"
                [onSelect]="onSelect"
                [items]="filtered()"
                [staticItems]="staticItems"
                [autoOpen]="autoOpen"
                [alwaysOpen]="alwaysOpen"
                [hideSearch]="hideSearch"
                [focusFirst]="false"
                [searchEnabled]="true"
                [apiBean]="apiBean"
                [placeholder]="'Select a Form'"
                [resetSelectedOnCleanup]="resetSelectedOnCleanup"
                [filter]="filter"
                [ariaLabel]="ariaLabel"
                (valueChange)="onValueChange($event)"
                [readOnly]="readOnly"
                [hasNoneItem]="hasNoneItem"
            />
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25IngestionPointDropdownComponent extends S25DropdownAbstract {
    readonly category = input<IngestionPointCategory>(); // Filter by ingestion point category
    readonly subCat = input<string>(); // Filter by ingestion point sub category
    readonly enabledOnly = input<boolean>(true); // Only show enabled forms by default
    readonly useFilter = input<"eventSurvey" | "registration">();
    /** When true, only forms with authless submissions enabled are listed. */
    readonly authlessOnly = input<boolean>(false);
    /** When set, drops that ingestion point from the list (e.g. hide the current form when picking a different target). */
    readonly excludeIngestionPointId = input<number>();

    readonly points = s25Resource({
        params: () => ({
            category: this.category(),
            subCategory: this.subCat(),
        }),
        loader: async ({ params: { category, subCategory }, abortSignal }) => {
            return DataIngestionService.getIngestionPoints(category, subCategory, false);
        },
    });

    readonly filtered = computed(() => {
        let forms = this.points.value() || [];
        forms.sort(S25Util.shallowSort("ingestionPointName"));

        // Apply filters
        if (this.enabledOnly()) {
            forms = forms.filter((form) => form.enabled !== false);
        }

        if (this.category()) {
            forms = forms.filter((form) => form.category === this.category());
        }

        if (this.subCat()) {
            forms = forms.filter((form) => form.subCat === this.subCat());
        }

        if (this.excludeIngestionPointId() != null) {
            forms = forms.filter((form) => form.ingestionPointId !== this.excludeIngestionPointId());
        }

        forms = forms.filter((form) => !form.deleted);

        if (this.useFilter()) {
            forms = forms.filter((form) => {
                if (!form.use) return false;
                if (this.useFilter() === "eventSurvey") return !!form.use.eventSurvey;
                if (this.useFilter() === "registration") return !!form.use.registration;
                return false;
            });
        }

        if (this.authlessOnly()) {
            forms = forms.filter((form) => form.authless === true);
        }

        // Convert to dropdown items
        return forms.map((form) => {
            return {
                txt: form.ingestionPointName || `Form ${form.ingestionPointId}`,
                val: form.ingestionPointId,
                itemTypeId: INGESTION_POINT_TYPE_ID,
                itemId: form.ingestionPointId,
                itemName: form.ingestionPointName,
                data: form,
            };
        });
    });

    constructor() {
        super();

        effect(() => {
            // Run onLoad hook when new points load
            if (this.points.value()) {
                this.onLoad?.();
            }
        });

        effect(() => {
            // this.items isn't used, but it's the canonical items property, so we can at least keep it updated
            this.items = this.filtered();
        });
    }
}
